/**
 * Sprint 53: Field Comparison + Portfolio Analytics.
 *
 * Shows all fields ranked by DB I/ha with sortable comparison table,
 * scatter plot (Kosten vs Erlös), crop grouping, and AI insights.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SourceBadge from './SourceBadge';
import { getCropColor } from '../config/cropColors';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';


function vsBadgeClass(vs) {
  if (vs === null || vs === undefined) return 'text-[var(--aif-text-muted)]';
  return vs >= 0 ? 'text-green-400' : 'text-red-400';
}

export default function FieldComparisonView({ token }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');   // 'table' | 'scatter'
  const [sortKey, setSortKey] = useState('db_i_per_ha');
  const [sortDesc, setSortDesc] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch('/api/v1/aif/farm/fields/comparison', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">
        {t('dashboard.loading')}
      </div>
    );
  }
  if (!data?.fields?.length) {
    return (
      <div className="text-xs text-[var(--aif-text-muted)] text-center py-8">
        {t('comparison.noData')}
      </div>
    );
  }

  const { fields, farm_averages: avgs, ktbl_benchmark: ktbl, insights } = data;

  // Client-side sort
  const sorted = [...fields].sort((a, b) => {
    const av = a[sortKey] ?? (sortDesc ? -999999 : 999999);
    const bv = b[sortKey] ?? (sortDesc ? -999999 : 999999);
    return sortDesc ? bv - av : av - bv;
  });

  const handleSort = (key) => {
    if (sortKey === key) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(true); }
  };

  // Crop grouping for crop comparison cards
  const cropMap = {};
  for (const f of fields) {
    if (!f.crop) continue;
    if (!cropMap[f.crop]) cropMap[f.crop] = { crop_name: f.crop_name, items: [] };
    cropMap[f.crop].items.push(f);
  }
  const cropList = Object.entries(cropMap)
    .map(([code, g]) => {
      const withDb = g.items.filter(f => f.db_i_per_ha != null);
      return {
        code,
        crop_name: g.crop_name,
        field_count: g.items.length,
        total_area: g.items.reduce((s, f) => s + f.area_ha, 0),
        avg_db_i: withDb.length
          ? Math.round(withDb.reduce((s, f) => s + f.db_i_per_ha, 0) / withDb.length)
          : null,
      };
    })
    .sort((a, b) => (b.avg_db_i || -999999) - (a.avg_db_i || -999999));
  const maxCropDb = Math.max(...cropList.map(c => c.avg_db_i || 0), 1);

  // Scatter data
  const scatterData = fields
    .filter(f => f.direct_costs_per_ha && f.revenue_per_ha)
    .map(f => ({
      x: f.direct_costs_per_ha,
      y: f.revenue_per_ha,
      z: Math.max(f.area_ha * 2, 20),
      name: f.field_name,
      crop: f.crop,
      db: f.db_i_per_ha,
    }));

  const SortIcon = ({ k }) =>
    sortKey === k
      ? <span className="text-[var(--aif-color-primary)]">{sortDesc ? '↓' : '↑'}</span>
      : <span className="opacity-30">↕</span>;

  const thClass = 'p-2 font-medium text-[var(--aif-text-muted)] cursor-pointer select-none whitespace-nowrap';
  const thClassFixed = 'p-2 font-medium text-[var(--aif-text-muted)] whitespace-nowrap';

  return (
    <div className="space-y-4">

      {/* View mode toggle */}
      <div className="flex gap-2">
        {['table', 'scatter'].map(mode => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              viewMode === mode
                ? 'bg-[var(--aif-color-primary)] text-white'
                : 'bg-[var(--aif-bg-surface)] text-[var(--aif-text-muted)] border border-[var(--aif-border-default)]'
            }`}
          >
            {mode === 'table' ? t('comparison.table') : t('comparison.chart')}
          </button>
        ))}
      </div>

      {/* Farm summary strip */}
      {avgs && (
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { label: `${t('comparison.farmAvg')} DB I`, value: `${avgs.avg_db_i_per_ha?.toLocaleString('de-DE')} €/ha` },
            { label: t('comparison.totalDb'),  value: `${avgs.total_db_i?.toLocaleString('de-DE')} €` },
            { label: t('comparison.totalArea'), value: `${avgs.total_area_ha?.toFixed(0)} ha` },
          ].map(card => (
            <div key={card.label} className="rounded-xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] p-2">
              <div className="text-[10px] text-[var(--aif-text-muted)] leading-tight">{card.label}</div>
              <div className="text-sm font-bold text-[var(--aif-text-primary)] mt-0.5">{card.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Table View ── */}
      {viewMode === 'table' && (
        <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
          <table className="w-full text-xs min-w-[480px]">
            <thead>
              <tr className="bg-[var(--aif-bg-surface)] border-b border-[var(--aif-border-default)] text-left">
                <th className={thClassFixed + ' w-7'}>#</th>
                <th className={thClass} onClick={() => handleSort('field_name')}>
                  {t('comparison.field')} <SortIcon k="field_name" />
                </th>
                <th className={thClass + ' text-right'} onClick={() => handleSort('area_ha')}>
                  {t('comparison.area')} <SortIcon k="area_ha" />
                </th>
                <th className={thClassFixed + ' hidden sm:table-cell'}>{t('comparison.crop')}</th>
                <th className={thClass + ' text-right hidden md:table-cell'} onClick={() => handleSort('revenue_per_ha')}>
                  {t('comparison.revenuePerHa')} <SortIcon k="revenue_per_ha" />
                </th>
                <th className={thClass + ' text-right hidden md:table-cell'} onClick={() => handleSort('direct_costs_per_ha')}>
                  {t('comparison.costsPerHa')} <SortIcon k="direct_costs_per_ha" />
                </th>
                <th className={thClass + ' text-right'} onClick={() => handleSort('db_i_per_ha')}>
                  {t('comparison.dbIPerHa')} <SortIcon k="db_i_per_ha" />
                </th>
                <th className={thClassFixed + ' text-right'}>{t('comparison.vsFarmAvg')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((f, i) => (
                <tr
                  key={f.field_id}
                  className="border-t border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-surface)]/50 transition-colors"
                >
                  <td className="p-2 text-[var(--aif-text-muted)]">{i + 1}</td>
                  <td className="p-2 font-medium text-[var(--aif-text-primary)] max-w-[120px] truncate">
                    {f.field_name}
                  </td>
                  <td className="p-2 text-right text-[var(--aif-text-secondary)]">
                    {f.area_ha?.toFixed(0)} ha
                  </td>
                  <td className="p-2 text-[var(--aif-text-secondary)] hidden sm:table-cell">
                    {f.crop_name}
                  </td>
                  <td className="p-2 text-right text-[var(--aif-text-secondary)] hidden md:table-cell">
                    {f.revenue_per_ha?.toLocaleString('de-DE')}
                    <SourceBadge source={f.price_source} variant="inline" className="ml-0.5 opacity-70" />
                  </td>
                  <td className="p-2 text-right text-[var(--aif-text-secondary)] hidden md:table-cell">
                    {f.direct_costs_per_ha?.toLocaleString('de-DE')}
                  </td>
                  <td className="p-2 text-right font-semibold text-[var(--aif-text-primary)]">
                    {f.db_i_per_ha?.toLocaleString('de-DE') ?? '—'}
                    <SourceBadge source={f.yield_source} variant="inline" className="ml-0.5 opacity-70" />
                  </td>
                  <td className={`p-2 text-right font-medium ${vsBadgeClass(f.vs_farm_avg)}`}>
                    {f.vs_farm_avg != null
                      ? `${f.vs_farm_avg > 0 ? '+' : ''}${f.vs_farm_avg?.toLocaleString('de-DE')}`
                      : '—'}
                  </td>
                </tr>
              ))}
              {/* Farm average footer row */}
              {avgs && (
                <tr className="border-t-2 border-[var(--aif-color-primary)]/30 bg-[var(--aif-bg-surface)]">
                  <td className="p-2" />
                  <td className="p-2 font-semibold text-xs text-[var(--aif-text-muted)]">
                    {t('comparison.farmAvg')}
                  </td>
                  <td className="p-2 text-right text-xs text-[var(--aif-text-muted)]">
                    {avgs.total_area_ha?.toFixed(0)} ha
                  </td>
                  <td className="p-2 hidden sm:table-cell" />
                  <td className="p-2 text-right text-xs font-medium text-[var(--aif-text-primary)] hidden md:table-cell">
                    {avgs.avg_revenue_per_ha?.toLocaleString('de-DE')}
                  </td>
                  <td className="p-2 text-right text-xs font-medium text-[var(--aif-text-primary)] hidden md:table-cell">
                    {avgs.avg_costs_per_ha?.toLocaleString('de-DE')}
                  </td>
                  <td className="p-2 text-right text-xs font-bold text-[var(--aif-color-primary)]">
                    {avgs.avg_db_i_per_ha?.toLocaleString('de-DE')}
                  </td>
                  <td className="p-2 text-right text-xs text-[var(--aif-text-muted)]">–</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Scatter Chart View ── */}
      {viewMode === 'scatter' && scatterData.length > 0 && (
        <div className="rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-3">
          <div className="text-[10px] text-[var(--aif-text-muted)] text-center mb-2">
            X: {t('comparison.xAxis')} · Y: {t('comparison.yAxis')}
          </div>
          <div data-testid="scatter-chart">
            <ResponsiveContainer width="100%" height={220}>
              <ScatterChart>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.15)" />
                <XAxis
                  dataKey="x"
                  type="number"
                  tick={{ fontSize: 9 }}
                  tickFormatter={v => `${v}`}
                />
                <YAxis
                  dataKey="y"
                  type="number"
                  tick={{ fontSize: 9 }}
                  tickFormatter={v => `${v}`}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] rounded-lg p-2 text-xs shadow-lg">
                        <div className="font-semibold mb-1">{d.name}</div>
                        <div>{t('comparison.costsPerHa')}: {d.x?.toLocaleString('de-DE')} €</div>
                        <div>{t('comparison.revenuePerHa')}: {d.y?.toLocaleString('de-DE')} €</div>
                        <div className="font-bold text-[var(--aif-color-primary)]">
                          DB I: {d.db?.toLocaleString('de-DE')} €/ha
                        </div>
                      </div>
                    );
                  }}
                />
                {avgs?.avg_costs_per_ha && (
                  <ReferenceLine x={avgs.avg_costs_per_ha} stroke="rgba(156,163,175,0.5)" strokeDasharray="4 2" />
                )}
                {avgs?.avg_revenue_per_ha && (
                  <ReferenceLine y={avgs.avg_revenue_per_ha} stroke="rgba(156,163,175,0.5)" strokeDasharray="4 2" />
                )}
                <Scatter
                  data={scatterData}
                  shape={(props) => {
                    const { cx, cy, payload } = props;
                    const r = Math.min(Math.sqrt(payload.z) * 0.8, 20);
                    const color = getCropColor(payload.crop).bg;
                    return (
                      <circle
                        cx={cx} cy={cy} r={r}
                        fill={color} fillOpacity={0.7}
                        stroke={color} strokeWidth={1}
                      />
                    );
                  }}
                />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          {/* Crop legend */}
          <div className="flex gap-3 flex-wrap justify-center mt-2">
            {cropList.map(c => (
              <div key={c.code} className="flex items-center gap-1 text-[10px] text-[var(--aif-text-muted)]">
                <span
                  className="w-2 h-2 rounded-full inline-block flex-shrink-0"
                  style={{ backgroundColor: getCropColor(c.code).bg }}
                />
                {c.crop_name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Crop Comparison Cards ── */}
      {cropList.length > 1 && (
        <div>
          <h4 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
            {t('comparison.cropComparison')}
          </h4>
          <div className="space-y-1.5">
            {cropList.map(c => (
              <div
                key={c.code}
                className="flex items-center gap-2 rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] px-3 py-2"
                data-testid="crop-comparison-card"
              >
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: getCropColor(c.code).bg }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs font-medium text-[var(--aif-text-primary)]">
                      {c.crop_name}
                    </span>
                    <span className="text-[10px] text-[var(--aif-text-muted)]">
                      {c.field_count} {t('comparison.fields')} · {c.total_area.toFixed(0)} ha
                    </span>
                  </div>
                  {c.avg_db_i != null && (
                    <div className="mt-1 h-1 bg-[var(--aif-border-default)] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-[var(--aif-color-primary)]"
                        style={{ width: `${Math.min((c.avg_db_i / maxCropDb) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xs font-bold text-[var(--aif-text-primary)]">
                    {c.avg_db_i?.toLocaleString('de-DE') ?? '—'} €/ha
                  </div>
                  {ktbl?.[c.code] && (
                    <div className="text-[10px] text-[var(--aif-text-muted)]">
                      KTBL: {ktbl[c.code].reference?.toLocaleString('de-DE')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── AI Insights ── */}
      {insights?.length > 0 && (
        <div
          className="rounded-xl border border-[var(--aif-color-primary)]/20 bg-[var(--aif-color-primary)]/5 p-3"
          data-testid="insights-section"
        >
          <h4 className="text-xs font-semibold text-[var(--aif-color-primary)] mb-2">
            {t('comparison.insights')}
          </h4>
          <div className="space-y-2">
            {insights.map((ins, i) => (
              <div key={i} className="flex gap-2 text-xs text-[var(--aif-text-secondary)]">
                <span className="flex-shrink-0">
                  {ins.type === 'best_field' ? '🏆' : ins.type === 'worst_field' ? '⚠️' : '📊'}
                </span>
                <span>{ins.text}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
